import { useState } from 'react'
import { Divider } from '@mui/material'
import { styled } from '@mui/material/styles'
import { Section, SectionTitle, Row, RowText, RowLabel } from '../../Profile/profile-styles'
import { MovieceptionOutlinedButton } from '../../../components/Buttons/movieception-buttons'
import { ConfirmModal } from '../../../utils/confirm-modal'
import { useSnackbar } from '../../../context/snackbar-context'
import { useAuth } from '../../../context/AuthContext'
import instance from '../../../axios'

const SectionDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
}))

export const DataSection = () => {
  const [clearHistoryOpen, setClearHistoryOpen] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const { logout } = useAuth()

  // Downloads everything the user has stored (profile, history, watchlist,
  // ratings, comments) as a single JSON file.
  const handleExport = async () => {
    setExporting(true)
    try {
      const res = await instance.get('/users/export')
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: 'application/json',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `movieception-export-${new Date().toISOString().slice(0, 10)}.json`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
    } finally {
      setExporting(false)
    }
  }

  const handleClearHistory = async () => {
    setClearing(true)
    try {
      await instance.delete('/history')
      // Media state (watched sets, stats) is cached per session - drop it and
      // reload so every page reflects the empty history.
      sessionStorage.removeItem('media-state-cache')
      window.location.reload()
    } catch {
      enqueueSnackbar('Could not clear watch history. Please try again.', {
        variant: 'error',
      })
      setClearing(false)
      setClearHistoryOpen(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      await instance.delete('/users/me')
      // Account is disabled server-side (soft delete); log out clears local
      // storage and the Auth0 session. Sign-in stays blocked until support reopens it.
      logout()
    } catch {
      enqueueSnackbar('Could not delete your account. Please try again.', {
        variant: 'error',
      })
      setDeleting(false)
      setDeleteAccountOpen(false)
    }
  }

  return (
    <>
      <Section>
        <SectionTitle>Data</SectionTitle>

        <Row>
          <RowText><RowLabel>Export My Data</RowLabel></RowText>
          <MovieceptionOutlinedButton
            onClick={handleExport}
            disabled={exporting}
            label={exporting ? 'Exporting…' : 'Export'}
          />
        </Row>

        <Row>
          <RowText><RowLabel>Clear Watch History</RowLabel></RowText>
          <MovieceptionOutlinedButton onClick={() => setClearHistoryOpen(true)} label='Clear' />
        </Row>

        <Row>
          <RowText><RowLabel>Reset Recommendations</RowLabel></RowText>
          {/* Recommendations aren't personalized yet - nothing meaningful to reset. */}
          <MovieceptionOutlinedButton label='Reset' disabled />
        </Row>

        <SectionDivider />

        <Row>
          <RowText><RowLabel>Danger zone</RowLabel></RowText>
          <MovieceptionOutlinedButton onClick={() => setDeleteAccountOpen(true)} label='Delete Account' />
        </Row>
      </Section>

      <ConfirmModal
        open={clearHistoryOpen}
        title='Clear Watch History'
        description='This removes every movie, show, and episode from your watch history.'
        warning="This can't be undone."
        confirmText={clearing ? 'Clearing…' : 'Clear History'}
        danger
        confirmDisabled={clearing}
        onConfirm={handleClearHistory}
        onCancel={() => setClearHistoryOpen(false)}
      />
      <ConfirmModal
        open={deleteAccountOpen}
        title='Delete Account'
        description='Your account will be disabled and you will be signed out. It can only be reopened by contacting support.'
        warning="This can't be undone."
        confirmText={deleting ? 'Deleting…' : 'DELETE'}
        danger
        countdownSeconds={10}
        confirmDisabled={deleting}
        onConfirm={handleDeleteAccount}
        onCancel={() => setDeleteAccountOpen(false)}
      />
    </>
  )
}
